import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { Card, Heading } from '@diwata/ironvale'
import { FLOW, GLYPH_KEY, SURFACES, THRESHOLDS, type FlowStage } from '@/content/map'
import './map.css'

// How it connects (P12-T03, redesigned) — a fixed, explanatory flow of the verse
// in the operator's own words. The marquee is a single left-to-right pipeline: the
// shared groundwork on the outside, your private verse on the inside, and a gate of
// judgment between them. No internal service names appear anywhere — only what a
// person does and sees. This is a hand-authored diagram, NOT the entity graph.

const GROUNDWORK = FLOW.filter((s) => s.zone === 'groundwork')
const ASKING = FLOW.find((s) => s.zone === 'gate')
const YOURS = FLOW.filter((s) => s.zone === 'yours')

function FlowNode({ stage, asking }: { stage: FlowStage; asking?: boolean }) {
  const className = ['dw-mapflow__node', asking ? 'dw-mapflow__node--asking' : '']
    .filter(Boolean)
    .join(' ')
  const inner = (
    <>
      <span className="dw-mapflow__glyph" aria-hidden="true">
        <span className="dw-mapflow__ink">{stage.glyph}</span>
      </span>
      <span className="dw-mapflow__name">{stage.name}</span>
      <span className="dw-mapflow__role">{stage.role}</span>
      <span className="dw-mapflow__detail">{stage.detail}</span>
    </>
  )
  return stage.to ? (
    <Link to={stage.to} className={className}>
      {inner}
    </Link>
  ) : (
    <div className={className}>{inner}</div>
  )
}

function NodeRow({ stages }: { stages: { stage: FlowStage; asking?: boolean }[] }) {
  return (
    <div className="dw-mapflow__nodes">
      {stages.map(({ stage, asking }, i) => (
        <Fragment key={stage.name}>
          <FlowNode stage={stage} asking={asking} />
          {i < stages.length - 1 && (
            <span className="dw-mapflow__arrow" aria-hidden="true">
              →
            </span>
          )}
        </Fragment>
      ))}
    </div>
  )
}

export function MapPage() {
  const leftNodes = [
    ...GROUNDWORK.map((stage) => ({ stage })),
    ...(ASKING ? [{ stage: ASKING, asking: true }] : []),
  ]

  return (
    <div className="dw-map">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">How it connects</div>
          <div className="dw-page-subtitle">
            The whole thing is one flow — the shared world on the outside, your world on the
            inside, and a gate you hold in between.
          </div>
        </div>
      </div>

      {/* ── The flow ─────────────────────────────────────────────────────── */}
      <section className="dw-map__section">
        <Heading level={2}>The flow</Heading>
        <p className="dw-map__lead">
          Everything the app does is one loop. Public sources fill a shared pool; when something
          crosses the rules you set, it asks for your judgment; whatever you keep becomes yours and
          powers real work — and nothing crosses into your world without you.
        </p>

        <div className="dw-mapflow">
          <div className="dw-mapflow__rail">
            <section className="dw-mapflow__group" aria-label="The shared groundwork">
              <div className="dw-mapflow__grouplabel">The shared groundwork</div>
              <NodeRow stages={leftNodes} />
            </section>

            <div className="dw-mapflow__gate" aria-hidden="true">
              <span className="dw-mapflow__gate-mark">◇</span>
              <span className="dw-mapflow__gate-title">You judge</span>
              <span className="dw-mapflow__gate-sub">nothing crosses without your gate</span>
            </div>

            <section className="dw-mapflow__group dw-mapflow__group--yours" aria-label="Your private verse">
              <div className="dw-mapflow__grouplabel">Your private verse</div>
              <NodeRow stages={YOURS.map((stage) => ({ stage }))} />
            </section>
          </div>

          <div className="dw-mapflow__familiar">
            <span className="dw-mapflow__familiar-mark" aria-hidden="true">
              ✦
            </span>
            <span className="dw-mapflow__familiar-text">
              <strong>The Familiar</strong> is present the whole way across — it reads the shared
              pool and your world, drafts and proposes, and backs up its work with sources. It never
              decides. Every gate on this page stays yours.
            </span>
          </div>
        </div>
      </section>

      {/* ── Three thresholds ─────────────────────────────────────────────── */}
      <section className="dw-map__section">
        <Heading level={2}>Three moments of commitment</Heading>
        <p className="dw-map__lead">
          A thing becomes more real to you in three steps — and you choose each one.
        </p>
        <div className="dw-map__thresholds">
          {THRESHOLDS.map((t) => (
            <Card key={t.step} className="dw-map__threshold">
              <div>
                <div className="dw-map__threshold-step">{t.step}</div>
                <div className="dw-map__threshold-means">{t.means}</div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ── The surfaces ─────────────────────────────────────────────────── */}
      <section className="dw-map__section">
        <Heading level={2}>The surfaces</Heading>
        <p className="dw-map__lead">Where each part of the flow lives. Every card opens its page.</p>
        <div className="dw-map__surfaces">
          {SURFACES.map((s) => (
            <Link key={s.name} to={s.to} className="iv-card iv-card--base dw-map__surface">
              <div className="dw-map__surface-head">
                <span className="dw-map__surface-glyph" aria-hidden="true">
                  {s.glyph}
                </span>
                <span className="dw-map__surface-name">{s.name}</span>
                <span className="dw-map__surface-go" aria-hidden="true">
                  →
                </span>
              </div>
              <div className="dw-map__surface-what">{s.what}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── The marks ────────────────────────────────────────────────────── */}
      <section className="dw-map__section">
        <Heading level={2}>The marks</Heading>
        <p className="dw-map__lead">A small key, so a glyph never leaves you guessing.</p>
        <Card>
          <div className="dw-map__key">
            {GLYPH_KEY.map((k) => (
              <div key={k.label} className="dw-map__key-row">
                <span className="dw-map__key-glyph" aria-hidden="true">
                  {k.glyph}
                </span>
                <span className="dw-map__key-label">{k.label}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  )
}
